import { useCallback } from 'react';
import { settingsService } from '../../../services/settingsService';
import { useSnackbar } from '../../../components/common/Snackbar';
import { extractErrorMessage } from '../../../utils/settingsUtils';

/**
 * Hook to manage notification toggle logic for Settings page
 * @param {Object} params - Hook parameters
 * @param {Object} params.notificationSettings - Notification settings object
 * @param {Function} params.setNotificationSettings - Setter for notification settings
 * @param {Function} params.setIsSavingEmailNotification - Setter for saving state
 * @returns {Function} Notification toggle handler
 */
export const useSettingsNotifications = ({
  notificationSettings,
  setNotificationSettings,
  setIsSavingEmailNotification,
}) => {
  const { showSnackbar } = useSnackbar();

  const handleNotificationToggle = useCallback(async (setting) => {
    // For email notifications, save to backend
    if (setting === 'emailNotifications') {
      const newValue = !notificationSettings.emailNotifications;

      // Optimistically update UI
      setNotificationSettings((prev) => ({
        ...prev,
        [setting]: newValue,
      }));

      setIsSavingEmailNotification(true);

      try {
        const response = await settingsService.updateNotificationPreferences({
          emailNotifications: newValue,
        });

        // Update with the actual response from server
        if (response && response.emailNotifications !== undefined) {
          setNotificationSettings((prev) => ({
            ...prev,
            emailNotifications: response.emailNotifications,
          }));
        }

        // Show success message - use backend message if available, otherwise custom
        const successMessage = response?.message ||
          (newValue
            ? 'Email notifications enabled successfully'
            : 'Email notifications disabled successfully');
        showSnackbar(successMessage, 'success');
      } catch (error) {
        // Revert on error
        setNotificationSettings((prev) => ({
          ...prev,
          [setting]: !newValue,
        }));

        const errorMessage = extractErrorMessage(error, 'Failed to update email notification preference');
        showSnackbar(errorMessage, 'error');
        // console.error('Error updating email notification preference:', error);
      } finally {
        setIsSavingEmailNotification(false);
      }
    } else {
      // For other notifications, just update local state for now
      setNotificationSettings((prev) => ({
        ...prev,
        [setting]: !prev[setting],
      }));
    }
  }, [notificationSettings, setNotificationSettings, setIsSavingEmailNotification, showSnackbar]);

  return {
    handleNotificationToggle,
  };
};
